import React, { useRef, useState } from "react";
import useEventAttendeeListLogic, {
  EventAttendee,
} from "./EventAttendeeList.logic";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableFilterMeta,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { ColumnMeta, exportCSVManual } from "../../../utils/exportCsv";
import { exportEventPdf } from "../../../utils/exportEventPdf";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";
import { confirmPopup } from "primereact/confirmpopup";

const EventAttendeeList: React.FC = () => {
  const {
    isAdmin,
    attendees,
    eventDetails,
    loading,
    navigate,
    paymentReceiptLoading,
    handleShowPaymentReceipt,
    totalDonationAmount,
    totalAmount,
    handleMarkAsPaid,
    handleMarkAsUnPaid,
  } = useEventAttendeeListLogic();

  const statusBodyTemplate = (rowData: EventAttendee) => {
    return rowData.status == "registered" ? (
      <span className="p-tag p-tag-success">{rowData.status_label}</span>
    ) : (
      <span className="p-tag p-tag-danger">{rowData.status_label}</span>
    );
  };

  const amountBodyTemplate = (rowData: EventAttendee) => {
    return (
      <div>
        <div>
          {rowData.donation_amount > 0 ? (
            <>
              {(rowData.amount ?? 0).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </>
          ) : (
            "Free"
          )}
        </div>
        {rowData.donation_amount > 0 && (
          <small className="text-muted">
            Donation:{" "}
            {rowData.donation_amount.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </small>
        )}
      </div>
    );
  };

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    // @ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const dt = useRef<DataTable<EventAttendee[]>>(null);

  const exportCSV = () => {
    const cols: ColumnMeta[] = [
      { field: "name", title: "Name" },
      { field: "email", title: "Email" },
      { field: "registered_at", title: "Registered At" },
      { field: "cancelled_at", title: "Cancelled At" },
      { field: "amount", title: "Amount Paid" },
      { field: "donation_amount", title: "Donation" },
      { field: "status", title: "Registration Status" },
    ];

    exportCSVManual(
      attendees.map((v) => ({
        name: v.User.name,
        email: v.User.email,
        registered_at: v.registered_at_formatted,
        cancelled_at: v.cancelled_at_formatted,
        amount: v.amount > 0 ? v.amount : "Free",
        donation_amount: v.donation_amount,
        status: v.status_label,
      })),
      cols,
      `event-attendees-${eventDetails.title}.csv`
    );
  };

  const exportPdf = () => {
    exportEventPdf({
      docTitle: "Event Attendee List",
      tableHeaders: [
        "Sr.",
        "Name",
        "Email",
        "Registered At",
        "Cancelled At",
        "Amount Paid",
        "Donation",
        "Registration Status",
      ],
      tableData: attendees.map((v, i) => [
        i + 1,
        v.User.name,
        v.User.email,
        v.registered_at_formatted,
        v.cancelled_at_formatted,
        v.amount > 0
          ? (v.amount ?? 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })
          : "Free",
        v.donation_amount > 0
          ? v.donation_amount.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })
          : 0,
        v.status_label,
      ]),
      eventDetails,
      filenamePrefix: "event-attendees",
    });
  };

  const renderHeader = () => {
    return (
      <>
        <div className="flex justify-content-start align-items-center mb-2 flex-column-responsive">
          <IconField iconPosition="left">
            <InputIcon className="fas fa-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Keyword Search"
            />
          </IconField>
          <>
            <Button
              type="button"
              icon="fas fa-file-csv"
              onClick={() => exportCSV()}
              tooltip="Export as CSV"
              tooltipOptions={{ position: "top" }}
              className="alt_btn ms-2"
            />
            <Button
              type="button"
              icon="fas fa-file-pdf"
              onClick={exportPdf}
              tooltip="Export as PDF"
              tooltipOptions={{ position: "top" }}
              className="alt_btn ms-2"
            />
            <div className="ml-auto">
              {isAdmin && totalAmount && totalAmount > 0 && (
                <Button
                  type="button"
                  label="Event Breakdown"
                  icon="fas fa-dollar-sign"
                  onClick={() => onShowBreakdown()}
                  className="alt_btn "
                />
              )}
              <Button
                type="button"
                label="Back to Event Detail"
                onClick={() => navigate(-1)}
                className="alt_btn ms-2"
              />
            </div>
          </>
        </div>
      </>
    );
  };

  const header = renderHeader();

  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const allowExpansion = (rowData: EventAttendee) => {
    return rowData.payment_id !== null;
  };

  const rowExpansionTemplate = (rowData: EventAttendee) => {
    return (
      <div className="container-fluid py-3 row-expansion-template">
        <div className="row">
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <h3 className="fw-bold">Payment Ref.:</h3>
            <p className="mb-2">{rowData.payment_id}</p>
          </div>
          {rowData.refund_id && (
            <div className="col-12 col-md-4 mb-3 mb-md-0">
              <h3 className="fw-bold">Refund Ref.:</h3>
              <p className="mb-2">{rowData.refund_id}</p>
            </div>
          )}
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <h3 className="fw-bold">Receipt:</h3>
            <a
              className="text-decoration-underline"
              onClick={() => handleShowPaymentReceipt(rowData.User.id ?? 0)}
            >
              {paymentReceiptLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <>
                  View <i className="fas fa-external-link-alt"></i>
                </>
              )}
            </a>
          </div>
        </div>
      </div>
    );
  };

  const [visibleBreakdown, setVisibleBreakdown] = useState(false);
  const onShowBreakdown = () => {
    setVisibleBreakdown(true);
  };
  const onHideBreakdown = () => {
    setVisibleBreakdown(false);
  };

  return (
    <>
      <section className="schedule-three ngo_list_wrapper">
        <div className="container">
          <div className="schedule-three__top">
            <div
              className="section-title text-center wow fadeInLeft"
              data-wow-delay="100ms"
            >
              <h2 className="section-title__title">Event Attendees</h2>
            </div>
          </div>
          <div className="schedule-three__bottom">
            <DataTable
              ref={dt}
              value={attendees}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              removableSort
              sortField="User.name"
              showGridlines
              paginator
              rows={5}
              loading={loading}
              rowsPerPageOptions={[5, 10, 25, 50]}
              filters={filters}
              // filterDisplay="row"
              globalFilterFields={["User.name", "User.email"]}
              header={header}
              emptyMessage="No attendees found."
              onFilter={(e) => setFilters(e.filters)}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Event Attendees"
              tableStyle={{ minWidth: "50rem" }}
            >
              <Column expander={allowExpansion} style={{ width: "5rem" }} />
              <Column field="User.name" header="Name" sortable></Column>
              <Column field="User.email" header="Email" sortable></Column>
              <Column
                field="registered_at_formatted"
                header="Registered At"
                sortable
              ></Column>
              <Column
                field="cancelled_at_formatted"
                header="Cancelled At"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                field="amount"
                header="Amount Paid"
                bodyStyle={{ textAlign: "center" }}
                body={amountBodyTemplate}
                headerClassName="custom-status-column"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "10%", minWidth: "8rem" }}
                field="status_label"
                header="Registration Status"
                bodyStyle={{ textAlign: "center" }}
                body={statusBodyTemplate}
                headerClassName="custom-status-column"
              ></Column>
            </DataTable>
          </div>
        </div>
      </section>
      <Dialog
        header="Event Breakdown"
        visible={visibleBreakdown}
        style={{ width: "40vw" }}
        onHide={onHideBreakdown}
        draggable={false}
      >
        <div>
          <p className="mb-1">
            <strong>Event:</strong> {eventDetails?.title}
          </p>
          <p className="mt-3 mb-1">
            <strong>Collected Amount:</strong> ${totalAmount}
          </p>
          <p className="mt-3 mb-1">
            <strong>Amount to Donate:</strong> ${totalDonationAmount}
          </p>
          <p className="mt-3 mb-2">
            <strong>Net Amount:</strong> ${totalAmount - totalDonationAmount}
          </p>
          <div className="ngo-img mt-3 mb-2">
            <strong>NGOs:</strong>
            {eventDetails?.event_ngos.map((ngo: any, index: number) => (
              <p key={index} className="mt-1">
                {index + 1}
                {". "}
                <a href={ngo.ngo.donation_link} target="_blank" key={index}>
                  {ngo.ngo.name} <i className="fas fa-external-link-alt"></i>{" "}
                </a>
                {ngo.is_paid ? (
                  <>
                    {"- "}
                    <a
                      className="text-decoration-underline"
                      onClick={() => handleMarkAsUnPaid(ngo.ngo_id)}
                    >
                      Mark as Unpaid
                    </a>{" "}
                    <span className="p-tag p-tag-success">Donation Paid</span>
                  </>
                ) : (
                  <>
                    {"- "}
                    <a
                      className="text-decoration-underline"
                      onClick={() => handleMarkAsPaid(ngo.ngo_id)}
                    >
                      Mark as Paid
                    </a>
                  </>
                )}
              </p>
            ))}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EventAttendeeList;
